import React, { useId, useMemo, useState } from 'react';
import WikiIcon from '../shared/WikiIcon';
import { WikiField, WikiSegmented } from '../modals/WikiModalShell';
import { WIKI_ACCESS_COPY, WIKI_ACL_ROLES } from './wikiAccessRules';
import './wikiAccess.css';

const COPY = WIKI_ACCESS_COPY;

/** "Specific roles" checklist. Admins are always on (they can't be locked out). */
export function WikiRolePicker({ value, onChange }) {
    const chosen = new Set(['admin', ...(value || [])]);
    const toggle = (role) => {
        const next = new Set(chosen);
        if (next.has(role)) next.delete(role); else next.add(role);
        next.add('admin');
        onChange(WIKI_ACL_ROLES.filter((item) => next.has(item)));
    };
    const missing = ![...chosen].some((role) => role !== 'admin');
    return (
        <div className="wk-acc-picker" data-testid="wiki-access-roles">
            <div className="wk-acc-checks" role="group" aria-label="Roles that can view">
                {WIKI_ACL_ROLES.map((role) => {
                    const locked = role === 'admin';
                    return (
                        <label key={role} className={`wk-acc-check${locked ? ' is-locked' : ''}`} data-role={role}>
                            <input
                                type="checkbox"
                                checked={chosen.has(role)}
                                disabled={locked}
                                onChange={() => toggle(role)}
                                data-testid={`wiki-access-role-${role}`}
                            />
                            <span>{COPY.roleLabels[role]}</span>
                            {locked && <span className="wk-t-meta wk-acc-check-note">{COPY.adminsAlways}</span>}
                        </label>
                    );
                })}
            </div>
            <div className={`wk-t-meta wk-modal-hint${missing ? ' wk-acc-warn' : ''}`}>
                {missing ? COPY.rolesNeedOne : COPY.rolesHint}
            </div>
        </div>
    );
}

/** "Specific people" picker over `GET /wiki/members` (only people who can edit). */
export function WikiPeoplePicker({ value, onChange, members, loading, viewRoles }) {
    const [query, setQuery] = useState('');
    const searchId = useId();
    const chosen = new Set(value || []);
    const pool = useMemo(() => (members || []).filter((member) => member.can_edit), [members]);
    const needle = query.trim().toLowerCase();
    const shown = needle
        ? pool.filter((member) => `${member.name} ${member.email}`.toLowerCase().includes(needle))
        : pool;
    const toggle = (id) => {
        const next = new Set(chosen);
        if (next.has(id)) next.delete(id); else next.add(id);
        onChange([...next]);
    };
    const roleCanView = (member) => !viewRoles || viewRoles.includes(member.role === 'sales_rep' ? 'user' : member.role);
    return (
        <div className="wk-acc-picker" data-testid="wiki-access-people">
            <label className="wk-sr-only" htmlFor={searchId}>{COPY.peopleSearch}</label>
            <input
                id={searchId}
                className="wk-modal-input wk-acc-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={COPY.peopleSearch}
                data-testid="wiki-access-people-search"
            />
            <ul className="wk-acc-people" aria-label="People who can edit">
                {loading && <li className="wk-t-meta wk-acc-people-empty">{COPY.peopleLoading}</li>}
                {!loading && shown.length === 0 && <li className="wk-t-meta wk-acc-people-empty">{COPY.peopleEmpty}</li>}
                {!loading && shown.map((member) => {
                    const admin = member.is_wiki_admin;
                    const on = admin || chosen.has(member.user_id);
                    return (
                        <li key={member.user_id}>
                            <label className={`wk-acc-person${admin ? ' is-locked' : ''}`} data-user-id={member.user_id}>
                                <input
                                    type="checkbox"
                                    checked={on}
                                    disabled={admin}
                                    onChange={() => toggle(member.user_id)}
                                    data-testid="wiki-access-person"
                                />
                                <span className="wk-acc-person-name">{member.name || member.email}</span>
                                <span className="wk-t-meta wk-acc-person-meta">
                                    {admin ? COPY.alreadyEdits : member.email}
                                </span>
                                {!admin && chosen.has(member.user_id) && !roleCanView(member) && (
                                    <span className="wk-t-meta wk-acc-warn">{COPY.roleCantView}</span>
                                )}
                            </label>
                        </li>
                    );
                })}
            </ul>
            <div className={`wk-t-meta wk-modal-hint${chosen.size === 0 ? ' wk-acc-warn' : ''}`}>
                {chosen.size === 0 ? COPY.peopleNeedOne : COPY.peopleHint}
            </div>
        </div>
    );
}

/**
 * WHO CAN VIEW / WHO CAN EDIT for a Wiki admin (#871). Every option maps to
 * real access rules (see wikiAccessRules.js); "Specific …" opens a picker.
 * Nested scopes (subcategory, page) start on "Same as {parent}".
 */
export default function WikiAccessFields({
    choices,
    onChange,
    nested = false,
    parentName = '',
    members = [],
    membersLoading = false,
    notCarried = [],
}) {
    const set = (patch) => onChange({ ...choices, ...patch });
    const viewOptions = [
        ...(nested ? [{ id: 'parent', label: COPY.view.parent(parentName) }] : []),
        { id: 'everyone', label: COPY.view.everyone },
        { id: 'roles', label: COPY.view.roles },
    ];
    const editOptions = [
        ...(nested ? [{ id: 'parent', label: COPY.edit.parent(parentName) }] : []),
        { id: 'editors', label: COPY.edit.editors },
        { id: 'admins', label: COPY.edit.admins },
        { id: 'people', label: COPY.edit.people },
    ];
    const viewRoles = choices.view === 'roles' ? ['admin', ...(choices.roles || [])] : null;
    return (
        <>
            <WikiField label={COPY.viewLabel} variant="group">
                <WikiSegmented
                    label={COPY.viewLabel}
                    options={viewOptions}
                    value={choices.view}
                    onChange={(view) => set({ view })}
                    testId="wiki-modal-view"
                />
                {choices.view === 'roles' && (
                    <WikiRolePicker value={choices.roles} onChange={(roles) => set({ roles })} />
                )}
            </WikiField>

            <WikiField label={COPY.editLabel} variant="group">
                <WikiSegmented
                    label={COPY.editLabel}
                    options={editOptions}
                    value={choices.edit}
                    onChange={(edit) => set({ edit })}
                    testId="wiki-modal-edit"
                />
                {choices.edit === 'people' && (
                    <WikiPeoplePicker
                        value={choices.people}
                        onChange={(people) => set({ people })}
                        members={members}
                        loading={membersLoading}
                        viewRoles={viewRoles}
                    />
                )}
            </WikiField>

            {notCarried.length > 0 && (
                <div className="wk-modal-note wk-acc-note" data-testid="wiki-access-not-carried">
                    <WikiIcon name="alert-triangle" size={14} />
                    <span>{COPY.notCarried(notCarried.join(', '))}</span>
                </div>
            )}
        </>
    );
}
